import React, { useState } from "react";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkIcon from "@mui/icons-material/Link";
import { gaEvent } from "../../analytics";

interface ShareButtonProps {
  name: string;
  productId?: string;
  size?: "small" | "medium";
}

const buildProductUrl = (productId: string) => `${window.location.origin}/producto/${productId}`;

const ShareButton: React.FC<ShareButtonProps> = ({ name, productId, size = "small" }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);

  const url = productId ? buildProductUrl(productId) : window.location.href;
  const text = `Mira este producto: ${name}`;

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    gaEvent("share_click", { item_name: name });

    if (navigator.share) {
      try {
        await navigator.share({ title: name, text, url });
        gaEvent("share_complete", { method: "native", item_name: name });
      } catch {
        /* user cancelled */
      }
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const shareWhatsApp = () => {
    gaEvent("share_complete", { method: "whatsapp", item_name: name });
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank");
    handleClose();
  };

  const shareFacebook = () => {
    gaEvent("share_complete", { method: "facebook", item_name: name });
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank",
    );
    handleClose();
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    gaEvent("share_complete", { method: "copy_link", item_name: name });
    setSnackOpen(true);
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleClick} size={size} aria-label="Compartir producto">
        <ShareIcon fontSize={size} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={shareWhatsApp}>
          <ListItemIcon>
            <WhatsAppIcon fontSize="small" sx={{ color: "brand.whatsapp" }} />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
        <MenuItem onClick={shareFacebook}>
          <ListItemIcon>
            <FacebookIcon fontSize="small" sx={{ color: "brand.facebook" }} />
          </ListItemIcon>
          <ListItemText>Facebook</ListItemText>
        </MenuItem>
        <MenuItem onClick={copyLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar enlace</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        message="Enlace copiado"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default ShareButton;
